import { isAxiosError } from 'axios';

import { applyCustomerCredit } from '../api/customerBalance';
import {
  addSalePayment,
  applyLineManualDiscount,
  applyOrderManualDiscount,
  confirmSalePayments,
  createSaleOrder,
} from '../api/saleOrders';
import { applyCommissionOverride } from '../api/commissionAgreements';
import { clampCurrency } from '../components/discountMath';
import { buildIdempotencyKey } from './idempotency';
import { resolveLineKey } from '../utils/lineKeys';
import type {
  ManualDiscountDraft,
  ManualDiscountRequestPayload,
  PosPaymentSummary,
  PosSaleLine,
  SaleItemOut,
  SaleOrderCreatePayload,
  SaleOrderItemPayload,
  SaleOrderOut,
  SalePaymentConfirmationOut,
  SalePaymentCreatePayload,
} from '../types';

const DEFAULT_ERROR_MESSAGE = 'No pudimos registrar la venta. Intentalo nuevamente.';

const buildManualDiscountPayload = (
  draft: ManualDiscountDraft | null | undefined,
  baseAmount: number,
): ManualDiscountRequestPayload | null => {
  if (!draft) {
    return null;
  }
  const reason = draft.reason?.trim();
  if (!reason) {
    return null;
  }
  const payload: ManualDiscountRequestPayload = {
    reason,
    note: draft.note?.trim() || null,
  };
  if (draft.mode === 'percentage') {
    const safeCoef = Math.max(0, Math.min(1, draft.value));
    payload.coef = Number(safeCoef.toFixed(4));
  } else {
    const safeAmount = clampCurrency(Math.min(baseAmount, Math.max(draft.value, 0)));
    payload.amount = safeAmount;
  }
  return payload;
};

interface SaleItemDraft {
  line: PosSaleLine;
  payload: SaleOrderItemPayload;
  subtotal: number;
  lineKey: string;
}

const buildSaleItemsDrafts = (lines: PosSaleLine[]): SaleItemDraft[] => {
  return lines
    .map((line) => {
      if (line.item.is_parent) {
        throw new Error(`Seleccioná una variante para ${line.item.name} antes de confirmar la venta.`);
      }
      const qty = Number(line.quantity);
      const safeQuantity = Number.isFinite(qty) ? qty : 0;
      const unitPrice = clampCurrency(line.useSubscription ? 0 : line.item.price?.value ?? 0);
      if (safeQuantity <= 0 || unitPrice < 0) {
        return null;
      }
      const requiresProfessional =
        line.item.item_type === 'service' && Boolean(line.item.service_detail?.is_appointment_based);
      if (requiresProfessional && line.professionalId == null) {
        throw new Error(
          `Asigná un profesional habilitado para el servicio ${line.item.name} antes de confirmar la venta.`,
        );
      }

      const payload: SaleOrderItemPayload = {
        item_id: line.item.id,
        qty: safeQuantity,
        unit_price: unitPrice,
      };
      if (line.manualDiscount?.mode === 'percentage') {
        payload.promotion_manual_discount_percent = Number(
          (Math.max(0, Math.min(1, line.manualDiscount.value)) * 100).toFixed(2),
        );
      } else if (line.manualDiscount?.mode === 'amount') {
        payload.manual_discount_amount = clampCurrency(Math.max(0, line.manualDiscount.value));
      }
      if (line.appointmentId != null) {
        payload.appointment_id = line.appointmentId;
      }
      if (line.professionalId != null) {
        payload.professional_id = line.professionalId;
      }
      const subtotal = clampCurrency(unitPrice * safeQuantity);
      return { line, payload, subtotal, lineKey: line.lineKey ?? resolveLineKey(line) };
    })
    .filter((draft): draft is SaleItemDraft => Boolean(draft));
};

const buildSaleItemKey = (
  itemId: number,
  quantity: number,
  unitPrice: number,
  appointmentId: number | null | undefined,
): string => {
  const safeQuantity = Number.isFinite(quantity) ? quantity : 0;
  const safePrice = clampCurrency(Number(unitPrice) || 0);
  const normalizedAppointment = appointmentId ?? 'none';
  return `${itemId}|${safeQuantity}|${safePrice}|${normalizedAppointment}`;
};

const buildDraftKey = (draft: SaleItemDraft): string => {
  const quantity =
    typeof draft.payload.qty === 'string' ? Number(draft.payload.qty) : draft.payload.qty;
  const unitPrice =
    typeof draft.payload.unit_price === 'string'
      ? Number(draft.payload.unit_price)
      : draft.payload.unit_price;
  return buildSaleItemKey(draft.payload.item_id, quantity, unitPrice, draft.payload.appointment_id);
};

const buildSnapshotKey = (saleItem: SaleItemOut): string => {
  const quantity = typeof saleItem.qty === 'string' ? Number(saleItem.qty) : Number(saleItem.qty ?? 0);
  const unitPrice =
    typeof saleItem.unit_price === 'string'
      ? Number(saleItem.unit_price)
      : Number(saleItem.unit_price ?? 0);
  return buildSaleItemKey(saleItem.item_id, quantity, unitPrice, saleItem.appointment_id);
};

const findSaleItemForDraft = (
  draftsKey: string,
  saleItems: SaleItemOut[],
  consumedIds: Set<number>,
): SaleItemOut => {
  const match = saleItems.find(
    (saleItem) => !consumedIds.has(saleItem.id) && buildSnapshotKey(saleItem) === draftsKey,
  );
  if (!match) {
    throw new Error('No pudimos vincular las líneas de venta devueltas por el backend.');
  }
  consumedIds.add(match.id);
  return match;
};

const resolveErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const detail = error.response?.data as
      | string
      | { detail?: string | { message?: string } | { code?: string; message?: string } }
      | undefined;
    if (typeof detail === 'string') {
      return detail;
    }
    const payloadDetail = detail?.detail;
    if (typeof payloadDetail === 'string') {
      return payloadDetail;
    }
    if (payloadDetail && typeof payloadDetail === 'object' && 'message' in payloadDetail) {
      const message = (payloadDetail as { message?: string }).message;
      if (message) {
        return message;
      }
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return DEFAULT_ERROR_MESSAGE;
};

const DEFAULT_COMMISSION_REASON = 'Ajuste manual desde POS';
const NO_COMMISSION_REASON = 'Venta sin comisión';
const PAYMENT_EPSILON = 0.009;
const MISSING_PAYMENT_METHOD_MESSAGE = 'Seleccioná un método de pago antes de confirmar la venta.';

const toNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildFinalPaymentSummary = (
  paymentSummary: PosPaymentSummary,
  finalOrderTotal: number,
  defaultPaymentMethodId: number | null = null,
  debtOnly = false,
): PosPaymentSummary => {
  if (debtOnly && paymentSummary.splits.length === 0) {
    return {
      ...paymentSummary,
      debtApplied: clampCurrency(Math.max(finalOrderTotal, 0)),
      outstanding: clampCurrency(Math.max(finalOrderTotal, 0)),
      changeDue: 0,
      totalTendered: 0,
    };
  }

  const creditApplied = clampCurrency(Math.max(paymentSummary.creditApplied, 0));
  const targetSplitTotal = clampCurrency(Math.max(finalOrderTotal - creditApplied, 0));
  const resolvePaymentMethodId = (): number | null => {
    const methodFromSplits = paymentSummary.splits.find((split) => split.methodId != null)?.methodId;
    return methodFromSplits ?? defaultPaymentMethodId ?? null;
  };
  const hasSingleAutoAssignedSplit =
    paymentSummary.splits.length === 1 &&
    paymentSummary.splits[0]?.methodId != null &&
    Math.abs(paymentSummary.totalTendered - paymentSummary.splits[0].amount - creditApplied) <= PAYMENT_EPSILON &&
    paymentSummary.outstanding <= PAYMENT_EPSILON &&
    paymentSummary.changeDue <= PAYMENT_EPSILON;

  let splits = hasSingleAutoAssignedSplit
    ? [{ ...paymentSummary.splits[0], amount: targetSplitTotal }]
    : paymentSummary.splits.map((split) => ({ ...split }));

  const hasValidSplit = splits.some(
    (split) => split.methodId != null && clampCurrency(Math.max(split.amount, 0)) > PAYMENT_EPSILON,
  );
  if (!hasValidSplit && targetSplitTotal > PAYMENT_EPSILON) {
    const methodId = resolvePaymentMethodId();
    if (methodId == null) {
      throw new Error(MISSING_PAYMENT_METHOD_MESSAGE);
    }
    splits = [
      {
        id: 'split-auto-final',
        methodId,
        amount: targetSplitTotal,
      },
    ];
  }

  const splitTotal = clampCurrency(
    splits.reduce((sum, split) => sum + clampCurrency(Math.max(split.amount, 0)), 0),
  );
  const totalTendered = clampCurrency(splitTotal + creditApplied);
  const outstanding = clampCurrency(Math.max(finalOrderTotal - totalTendered, 0));
  const changeDue = clampCurrency(Math.max(totalTendered - finalOrderTotal, 0));

  return {
    ...paymentSummary,
    splits,
    totalTendered,
    outstanding,
    changeDue,
    registerDebt: outstanding > PAYMENT_EPSILON,
    debtApplied: outstanding > PAYMENT_EPSILON ? outstanding : 0,
  };
};

export interface FinalizePosCheckoutParams {
  lines: PosSaleLine[];
  customerId: number | null;
  orderDiscount: ManualDiscountDraft | null;
  couponCode?: string | null;
  shippingMethodId: number | null;
  shippingAmountOverride?: number | null;
  paymentSummary: PosPaymentSummary;
  orderSubtotal: number;
  defaultPaymentMethodId?: number | null;
  debtOnly?: boolean;
  commissionsEnabled?: boolean;
  promotionPreview?: {
    token: string;
    source: 'promotion' | 'manual' | 'mixed' | 'none';
    snapshot: Record<string, unknown>;
    manual_global_won?: boolean;
  };
}

export interface FinalizePosCheckoutResult {
  order: SaleOrderOut;
  confirmation: SalePaymentConfirmationOut;
  saleItemMapping: Record<string, number>;
}

export const finalizePosCheckout = async ({
  lines,
  customerId,
  orderDiscount,
  couponCode,
  shippingMethodId,
  shippingAmountOverride,
  paymentSummary,
  orderSubtotal,
  defaultPaymentMethodId = null,
  debtOnly = false,
  commissionsEnabled = true,
  promotionPreview,
}: FinalizePosCheckoutParams): Promise<FinalizePosCheckoutResult> => {
  if (lines.length === 0) {
    throw new Error('Agregá al menos un ítem antes de confirmar la venta.');
  }

  const saleItemDrafts = buildSaleItemsDrafts(lines);
  if (saleItemDrafts.length === 0) {
    throw new Error('Las líneas de venta no son válidas para registrar la orden.');
  }

  const orderPayload: SaleOrderCreatePayload = {
    items: saleItemDrafts.map((draft) => draft.payload),
    customer_id: customerId ?? undefined,
    shipping_method_id: shippingMethodId ?? undefined,
    shipping_amount_override: shippingAmountOverride ?? undefined,
    takeaway: shippingMethodId == null,
    origin: 'erp',
    coupon_codes: couponCode?.trim() ? [couponCode.trim()] : undefined,
    idempotency_key: buildIdempotencyKey('order'),
    promotion_evaluation_token: promotionPreview?.token,
    promotion_snapshot: promotionPreview?.snapshot,
    promotion_source: promotionPreview?.source,
    promotion_order_discount: orderDiscount
      ? {
          mode: orderDiscount.mode,
          value: orderDiscount.mode === 'percentage' ? orderDiscount.value * 100 : orderDiscount.value,
          reason: orderDiscount.reason ?? null,
          note: orderDiscount.note ?? null,
        }
      : null,
  };

  let currentOrder = await createSaleOrder(orderPayload);
  const promotionBranchWon =
    promotionPreview?.source === 'promotion' || promotionPreview?.source === 'mixed';
  const manualGlobalReplacesLines = Boolean(promotionPreview?.manual_global_won);
  let saleItemsSnapshot = currentOrder.items ?? [];
  if (saleItemsSnapshot.length < saleItemDrafts.length) {
    throw new Error('El backend devolvió una cantidad inesperada de líneas de venta.');
  }

  const consumedSaleItemIds = new Set<number>();
  const saleItemMapping: Record<string, number> = {};

  for (const draft of saleItemDrafts) {
    const saleItemKey = buildDraftKey(draft);
    let saleItem = findSaleItemForDraft(saleItemKey, saleItemsSnapshot, consumedSaleItemIds);

    const manualDiscountPayload = promotionBranchWon || manualGlobalReplacesLines
      ? null
      : buildManualDiscountPayload(draft.line.manualDiscount, draft.subtotal);
    if (manualDiscountPayload) {
      currentOrder = await applyLineManualDiscount(currentOrder.id, saleItem.id, manualDiscountPayload);
      saleItemsSnapshot = currentOrder.items ?? [];
      const refreshed = saleItemsSnapshot.find((item) => item.id === saleItem.id);
      if (refreshed) {
        saleItem = refreshed;
      }
    }

    if (commissionsEnabled) {
      const commission = draft.line.commission;
      if (commission?.mode === 'manual') {
        await applyCommissionOverride(currentOrder.id, saleItem.id, {
          action: 'override',
          commission_amount: commission.amount,
          agent_id: commission.agentId ?? null,
          reason: commission.overrideReason ?? DEFAULT_COMMISSION_REASON,
          note: commission.overrideNote ?? undefined,
        });
      } else if (commission?.mode === 'none') {
        await applyCommissionOverride(currentOrder.id, saleItem.id, {
          action: 'no_commission',
          reason: commission.overrideReason ?? NO_COMMISSION_REASON,
          note: commission.overrideNote ?? undefined,
        });
      }
    }

    if (draft.lineKey) {
      saleItemMapping[draft.lineKey] = saleItem.id;
    }
  }

  const orderDiscountPayload = promotionBranchWon
    ? null
    : buildManualDiscountPayload(orderDiscount, clampCurrency(orderSubtotal));
  if (orderDiscountPayload) {
    currentOrder = await applyOrderManualDiscount(currentOrder.id, orderDiscountPayload);
  }

  const finalPaymentSummary = buildFinalPaymentSummary(
    paymentSummary,
    clampCurrency(toNumber(currentOrder.total_charged)),
    defaultPaymentMethodId,
    debtOnly,
  );
  const paymentSplits = finalPaymentSummary.splits ?? [];
  await Promise.all(
    paymentSplits
      .filter((split) => split.methodId != null)
      .map((split, index) => {
        const safeAmount = clampCurrency(split.amount);
        if (safeAmount <= 0) {
          return null;
        }
        const payload: SalePaymentCreatePayload = {
          payment_method_id: split.methodId as number,
          amount: safeAmount,
          idempotency_key: buildIdempotencyKey(`payment-${index + 1}`),
        };
        return addSalePayment(currentOrder.id, payload);
      })
      .filter(Boolean) as Promise<unknown>[],
  );

  const creditAmount = clampCurrency(finalPaymentSummary.creditApplied);
  if (creditAmount > 0) {
    await applyCustomerCredit(currentOrder.id, {
      amount: creditAmount,
      idempotency_key: buildIdempotencyKey('credit'),
    });
  }

  const confirmation = await confirmSalePayments(currentOrder.id, {
    register_debt: finalPaymentSummary.registerDebt,
    debt_note: finalPaymentSummary.debtNote ?? null,
    idempotency_key: buildIdempotencyKey('confirm'),
  });

  return {
    order: { ...currentOrder, status: confirmation.status },
    confirmation,
    saleItemMapping,
  };
};

export { resolveErrorMessage };
